/**
 * Extracts the most recent reply content from an email preview by removing
 * metadata, timestamps, and quoted replies from various email clients.
 *
 * Handles common email metadata patterns in multiple languages:
 * - "On [date] at [time] [sender] wrote:"
 * - "Le [date] à [time], [sender] a écrit :"
 * - "Am [date] um [time] schrieb [sender]:"
 * - "El [date] a las [time], [sender] escribió:"
 * - And other common patterns
 *
 * @param preview - The email preview text that may include metadata and quoted content
 * @returns The cleaned text content of the most recent reply
 *
 * @example
 * ```ts
 * formatEmailPreview("Inbound test 1On Tue, Jan 13, 2026 at 2:01PM Brayden...")
 * // Returns: "Inbound test 1"
 * ```
 */
export const formatEmailPreview = (preview: string): string => {
  if (!preview || typeof preview !== "string") {
    return "";
  }

  // Common patterns that indicate the start of quoted/forwarded content
  const metadataPatterns = [
    // English patterns - look for "On" followed by date-like patterns
    // Using more specific date patterns to avoid false positives
    /On\s+[A-Za-z]+,?\s+[A-Za-z]+\s+\d{1,2},?\s+\d{4}/i, // On Mon, Jan 13, 2026 or On Monday January 13 2026
    /On\s+[A-Za-z]+,?\s+[A-Za-z]+\s+\d{1,2}\s+at\s+\d{1,2}:\d{2}/i, // On Mon, Jan 13 at 2:00 PM (without year)
    /On\s+\d{1,2}\/\d{1,2}\/\d{2,4}/i, // On 1/13/2026
    /On\s+[A-Za-z]+\s+\d{1,2},?\s+\d{4}/i, // On Jan 13, 2026
    /From:\s*.*?Sent:/is, // Outlook style (s flag for multiline)
    /-----Original Message-----/i,
    /----- Forwarded message -----/i,

    // French patterns
    /Le\s+\d{1,2}\s+[a-zéû]+\s+\d{4}/i, // Le 13 janvier 2026
    /De\s*:.*?Envoyé\s*:/is,

    // German patterns
    /Am\s+\d{1,2}\.\s+[A-Za-zäöü]+\s+\d{4}/i, // Am 13. Januar 2026
    /Von\s*:.*?Gesendet\s*:/is,

    // Spanish patterns
    /El\s+\d{1,2}\s+de\s+[a-z]+\s+de\s+\d{4}/i, // El 13 de enero de 2026
    /De\s*:.*?Enviado\s*:/is,

    // Portuguese patterns
    /Em\s+\d{1,2}\s+de\s+[a-z]+\s+de\s+\d{4}/i, // Em 13 de janeiro de 2026

    // Chinese patterns
    /在.*?写道/i,

    // Generic fallback patterns (less specific, checked last)
    /\d{1,2}\/\d{1,2}\/\d{2,4}\s+(?:at\s+)?\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)/i,
    /\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/i,
  ];

  let cleanedPreview = preview;

  // Find the earliest match of any metadata pattern
  let earliestIndex = preview.length;
  let foundPattern = false;

  for (const pattern of metadataPatterns) {
    const match = preview.match(pattern);
    if (match && match.index !== undefined && match.index < earliestIndex) {
      earliestIndex = match.index;
      foundPattern = true;
    }
  }

  // If we found a metadata pattern, extract text before it
  if (foundPattern && earliestIndex > 0) {
    cleanedPreview = preview.substring(0, earliestIndex);
  }

  // Additional cleanup: remove common quote markers at the start
  cleanedPreview = cleanedPreview.replace(/^[\s>]+/, "");

  // Trim whitespace
  cleanedPreview = cleanedPreview.trim();

  return cleanedPreview;
};
